import { useMemo } from 'react';
import { useQuery } from '@common-fe/common-fe';
import dayjs from 'dayjs';
import { toString, uniqBy } from 'lodash';

import { api } from '@/api';
import { API_URL, IAccountOrderDto, IAccountOrderDtoAccountTypeEnum } from '@/dtos';
import { Service } from '@/modules/transaction/queries/useServices.query';
import { formatAccountOrder } from '@/modules/UseMyMoney/utils';

export interface AccountOrderCoveragePeriod {
  coverageDateStart?: string;
  coverageDateEnd?: string;
}

export interface AccountOrder {
  index: string;
  accountId?: string;
  order?: number;
  planName?: string;
  accountType?: IAccountOrderDtoAccountTypeEnum;
  submitClaimsDateEnd?: string;
  isForfeited?: boolean;
  availableBalance?: number;
  coveragePeriods?: AccountOrderCoveragePeriod[];
  services?: Service[];
}

interface Props {
  employeeId?: string;
  dependentId?: string;
  serviceOn?: string;
}

const useGetAccountOrdersQuery = ({ employeeId, dependentId, serviceOn }: Props) => {
  const { data, isLoading } = useQuery([API_URL.ACCOUNT_ORDERS_SERVICES, employeeId, serviceOn], () =>
    api.get<IAccountOrderDto[][]>(API_URL.ACCOUNT_ORDERS_SERVICES, {
      params: {
        employee_id: employeeId || undefined,
        service_on: serviceOn || undefined,
        ...dependentId ? { dependent_id: dependentId } : {}
      },
    }),
    {
      enabled: Boolean(employeeId && serviceOn),
    }
  );

  const accounts = useMemo(() => {
    const accountOrdersFlat = data?.data?.flat();
    const formattedAccountOrders = accountOrdersFlat?.map(formatAccountOrder);
    return formattedAccountOrders;
  }, [data?.data]);

  const accountOrdersArrays = useMemo((): AccountOrder[][] => {
    const accountOrdersArrays = data?.data;

    if (!accountOrdersArrays?.length) return [];

    const availableAccountOrdersArrays = accountOrdersArrays
      ?.map((accountOrders) => accountOrders?.filter((accountOrder) => !accountOrder?.is_forfeited));

    if (!availableAccountOrdersArrays?.length) return [];

    const sortedAccountOrdersArrays = availableAccountOrdersArrays
      ?.map((accountOrders) => accountOrders?.sort((prev, next) => ((prev?.order || 0) > (next?.order || 0) ? 1 : -1)));

    const accountOrdersArraysWithUniqServices = sortedAccountOrdersArrays?.map((accountOrders, index) => {
      const seenServiceIds = new Set<string>();

      return accountOrders.map((accountOrder) => {
        const formatted = formatAccountOrder(accountOrder, index);
        const uniqueServices = (formatted.services || []).filter((service) => {
          if (seenServiceIds.has(service.id)) return false;
          seenServiceIds.add(service.id);
          return true;
        });

        return { ...formatted, index: toString(index), services: uniqueServices };
      });
    });

    return accountOrdersArraysWithUniqServices;
  }, [data]);

  const availableAccounts = useMemo(() => {
    if (!accounts?.length) return [];
    const currentDate = dayjs().startOf('date').valueOf();

    const filteredAccountOrders = accounts
      ?.flat()
      ?.filter((accountOrder) => {
        const submitClaimsDateEnd = accountOrder?.submitClaimsDateEnd
          ? dayjs(accountOrder?.submitClaimsDateEnd).valueOf()
          : 0;
        const isClaimsDateExpired = submitClaimsDateEnd && submitClaimsDateEnd < currentDate;
        return !accountOrder?.isForfeited && accountOrder?.planName && !isClaimsDateExpired;
      })
      ?.sort((prev, next) => ((prev?.order || 0) > (next?.order || 0) ? 1 : -1));

    return filteredAccountOrders;
  }, [accounts]);

  const uniqAccounts = useMemo(() => uniqBy(availableAccounts, 'accountId'), [availableAccounts]);

  return {
    accountOrdersArrays,
    uniqAccounts,
    availableAccounts,
    isAccountOrdersLoading: isLoading,
  };
};

export default useGetAccountOrdersQuery;
